import * as React from 'react'

import { Box, Icon, SearchField, Spinner, Text } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/PopoverMenu'
import { Controls, InfiniteLoader } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

interface Props {
  open: boolean
  onOpenChange: () => void
  disabled: boolean
  placeholder: string
  displayValue?: string
  onSearchChange?: (value: string) => void
  loading?: boolean
  controls?: Controls
  hasOptions: boolean
  children: React.ReactNode
  readonly?: boolean
}

const PickerContainer = ({
  open,
  onOpenChange,
  disabled,
  placeholder,
  displayValue,
  onSearchChange,
  loading,
  controls,
  hasOptions,
  children,
  readonly,
}: Props) => {
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const showPlaceholder = !displayValue

  // Call onSearchChange when debounced search changes
  React.useEffect(() => {
    if (onSearchChange) onSearchChange(debouncedSearch)
  }, [debouncedSearch, onSearchChange])

  return (
    <Box flexGrow>
      <Popover type="dialog" open={open} onClick={onOpenChange}>
        <Popover.Trigger disabled={disabled || readonly}>
          <Box paddingX="s16" flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            <Text
              weight="normal"
              size="300"
              ellipsis
              color={showPlaceholder || readonly ? 'grey600' : 'textPrimary'}
            >
              {showPlaceholder ? placeholder : displayValue}
            </Text>
            <Icon.Small icon="caret-down" />
          </Box>
        </Popover.Trigger>
        <Popover.Content side="bottom" align="center" width="s400">
          {/* Search Field */}
          <Box
            width="full"
            paddingX="s12"
            paddingY="s8"
            border={{ bottom: { width: 's1', style: 'solid', color: 'grey200' } }}
          >
            <SearchField
              search={search}
              onSearch={onSearch}
              placeholder={i18n.t('common.search')}
            />
          </Box>

          {/* Content */}
          {loading ? (
            <Box
              gap="s8"
              paddingY="s16"
              width="full"
              height="s96"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Box>
          ) : (
            <>
              {hasOptions && (
                <>
                  {controls ? (
                    <Box maxHeight="s240" paddingX="s8" overflowY="auto" data-scroll-container>
                      <InfiniteLoader controls={controls}>{children}</InfiniteLoader>
                    </Box>
                  ) : (
                    <Box maxHeight="s240" paddingX="s8" overflowY="auto">
                      {children}
                    </Box>
                  )}
                </>
              )}

              {!hasOptions && (
                <Box alignItems="center" padding="s32" width="full">
                  <Text.Normal size="200" color="grey600">
                    {i18n.t('finance.employee_cost_centers_info.modals.set_cost_centers.not_found')}
                  </Text.Normal>
                </Box>
              )}
            </>
          )}
        </Popover.Content>
      </Popover>
    </Box>
  )
}

export default PickerContainer
